import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getBackend } from "../backend/backend2";
import { EXTRA_ENTITY_DATASOURCE_TYPE, EXTRA_ENTITY_DATA_TYPE } from "@/lib/ManufacturingConsts.generated";

const getQueryKey = (uuid: string) => ["comment", uuid] as const;

// Tracks the latest in-flight save per order uuid. Concurrent mutations for the same
// order (e.g. debounced inline edits) each optimistically update the cache; without
// this, a failed older mutation could roll back a newer one's value.
// TODO: Once we upgrade to TanStack Query v5, use mutation scopes instead?
const latestMutationGenerationByUuid = new Map<string, number>();

/**
 * Get the comment for an order uuid, returns a UseQueryResult.
 * When fallbackComment is provided and the cache is empty, it seeds the query
 * without a network request until the entry is invalidated (e.g. after a save).
 */
export const useGetOrderComment = (uuid: string, fallbackComment?: string) => {
    return useQuery<string>({
        queryKey: getQueryKey(uuid),
        staleTime: Infinity,
        enabled: Boolean(uuid),
        initialData: fallbackComment,
        queryFn: async () => {
            const res = await getBackend().manufacturing.searchOrders({
                include_skip_sim_internal: true,
                order_uuids: [uuid],
            });

            if (res.orders && res.orders.length > 0) {
                return res.orders[0].order_comment;
            }

            return "";
        },
    });
};

type OrderWithComment = {
    uuid: string;
    external_operation_id: string;
    order_comment: string;
};

type UpdateOrderCommentVariables = {
    order: OrderWithComment;
    comment: string;
};

type UpdateOrderCommentContext = {
    generation: number;
};

/**
 * Update the order comment, returns a UseMutationResult.
 */
export const useUpdateOrderComment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        onMutate: async ({ order, comment }: UpdateOrderCommentVariables): Promise<UpdateOrderCommentContext> => {
            const queryKey = getQueryKey(order.uuid);

            // Bump generation so onSettled can tell whether a later mutation superseded this one.
            const generation = (latestMutationGenerationByUuid.get(order.uuid) ?? 0) + 1;
            latestMutationGenerationByUuid.set(order.uuid, generation);

            await queryClient.cancelQueries({ queryKey });
            queryClient.setQueryData(queryKey, comment);

            return { generation };
        },

        mutationFn: async ({ order, comment }: UpdateOrderCommentVariables) => {
            await getBackend().manufacturing.upsertExtraEntity({
                datasource_type: EXTRA_ENTITY_DATASOURCE_TYPE.orders,
                external_id: order.external_operation_id,
                data_type: EXTRA_ENTITY_DATA_TYPE.comment,
                value: comment,
            });
        },

        onSettled: (_data, _error, { order }, context) => {
            // Ignore stale mutations: if B started after A, A must not reconcile the cache
            // when it settles (success or failure), or it would overwrite B's newer value.
            if (!context || context.generation !== latestMutationGenerationByUuid.get(order.uuid)) {
                return;
            }

            latestMutationGenerationByUuid.delete(order.uuid);

            // Refetch server truth instead of snapshot rollback — avoids restoring a value
            // that was itself an optimistic write from an earlier concurrent mutation.
            void queryClient.invalidateQueries({ queryKey: getQueryKey(order.uuid) });
        },
    });
};

/**
 * Prefer Redux comment unless a save mutation is in flight for this order,
 * so plan reloads are not overridden by stale React Query cache.
 */
export const useResolvedOrderComment = (order_uuid: string, comment: string) => {
    const { data: cachedComment } = useGetOrderComment(order_uuid, comment);
    const updateOrderComment = useUpdateOrderComment();

    const isMutatingThisOrder = updateOrderComment.isPending && updateOrderComment.variables?.order.uuid === order_uuid;
    const resolvedComment = isMutatingThisOrder ? (cachedComment ?? comment) : comment;

    return { resolvedComment, updateOrderComment };
};
